import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  PencilSimple,
  Trash,
  Camera,
  Drop,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AppHeader } from "@/components/AppHeader";
import { PhotoGallery } from "@/components/PhotoGallery";
import { ItemLinks } from "@/components/ItemLinks";
import { hasPendingHd, usePendingHdRefresh } from "@/lib/photoHd";
import { api, formatApiErrorDetail } from "@/lib/api";
import { shrinkAll } from "@/lib/shrinkImage";
import { useDirectory } from "@/lib/directory";

const SINK_ANGLES = [
  { value: "front", label: "Front" },
  { value: "side", label: "Side" },
  { value: "top", label: "Top" },
];

export default function SinkDetail() {
  const { id } = useParams();
  const { nameOf } = useDirectory();
  const navigate = useNavigate();
  const [sink, setSink] = useState(null);
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [editingNotes, setEditingNotes] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editingPrice, setEditingPrice] = useState(false);
  const [priceDraft, setPriceDraft] = useState({ cost: "", sale_price: "" });
  const [savingPrice, setSavingPrice] = useState(false);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const { data } = await api.patch(`/sinks/${id}/notes`, {
        notes: notesDraft,
      });
      setSink(data);
      setEditingNotes(false);
      toast.success("Notes saved");
    } catch {
      toast.error("Could not save notes");
    }
    setSavingNotes(false);
  };

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/sinks/${id}/pricing`, {
        cost: priceDraft.cost === "" ? null : Number(priceDraft.cost),
        sale_price:
          priceDraft.sale_price === "" ? null : Number(priceDraft.sale_price),
      });
      setSink(data);
      setEditingPrice(false);
      toast.success("Pricing saved");
    } catch {
      toast.error("Could not save pricing");
    }
    setSavingPrice(false);
  };

  const load = useCallback(async () => {
    const { data } = await api.get(`/sinks/${id}`);
    setSink(data);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  usePendingHdRefresh(hasPendingHd(sink?.photos), load);

  const revertHd = async (photo) => {
    try {
      const { data } = await api.post("/photos/revert-hd", {
        collection: "sinks",
        doc_id: id,
        photo_id: photo.id,
      });
      setSink(data);
      toast.success("Original photo restored");
    } catch {
      toast.error("Could not restore the original");
    }
  };

  if (!sink) {
    return (
      <div className="grain min-h-screen">
        <AppHeader title="Inventory / Sink" />
        <main className="relative z-10 pt-6">
          <div className="h-40 animate-pulse rounded-lg bg-zinc-900" />
        </main>
      </div>
    );
  }

  const photos = (sink.photos || []).filter((p) => !p.is_deleted);
  const reserved = (sink.allocations || []).reduce(
    (n, a) => n + (a.qty || 1),
    0,
  );
  const low = sink.stocked !== false && sink.quantity <= sink.min_level;

  const upload = async (e) => {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";
    if (!picked.length) return;
    setBusy(true);
    const fd = new FormData();
    (await shrinkAll(picked)).forEach((f) => fd.append("files", f));
    try {
      const { data } = await api.post(`/sinks/${id}/photos`, fd);
      setSink(data);
      toast.success(
        picked.length === 1 ? "Photo added" : `${picked.length} photos added`,
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Photo upload failed",
      );
    }
    setBusy(false);
  };

  const removePhoto = async (photoId) => {
    const { data } = await api.delete(`/sinks/${id}/photos/${photoId}`);
    setSink(data);
    toast.success("Photo removed");
  };

  const setAngle = async (p, angle) => {
    try {
      const { data } = await api.patch(`/sinks/${id}/photos/${p.id}`, { angle });
      setSink(data);
      toast.success(
        angle ? `Tagged as ${SINK_ANGLES.find((a) => a.value === angle)?.label}` : "Angle cleared",
      );
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err.response?.data?.detail) || "Could not tag photo",
      );
    }
  };
  const reorderPhotos = async (order) => {
    const { data } = await api.put(`/sinks/${id}/photos/order`, { order });
    setSink(data);
  };
  const setCover = async (p) => {
    const order = [p.id, ...photos.filter((x) => x.id !== p.id).map((x) => x.id)];
    const { data } = await api.put(`/sinks/${id}/photos/order`, { order });
    setSink(data);
    toast.success("Cover photo updated");
  };
  const tagged = new Set(photos.map((p) => p.angle).filter(Boolean));

  const release = async (a) => {
    const { data } = await api.delete(`/sinks/${id}/assign/${a.id}`);
    setSink(data);
    toast.success("Released back to stock");
  };

  const remove = async () => {
    await api.delete(`/sinks/${id}`);
    toast.success("Sink removed");
    navigate("/inventory/sinks");
  };

  const rows = [
    ["Item code", sink.code],
    ["Brand", sink.brand || "—"],
    ["Type", sink.sink_type],
    ["Bowls", sink.bowls],
    ["Size", sink.size || "—"],
    ["Cutout / inner size", sink.cutout_size || "—"],
    ["Material", sink.material],
    ["Gauge", sink.gauge ? `${sink.gauge} ga` : "—"],
    ["Availability", sink.special_order ? "Supplier Stock" : "Stock sink"],
    ["Supplier", sink.supplier || "—"],
    ["Shelf", sink.location || "—"],
    [
      "Stock type",
      sink.stocked === false
        ? "Supplier stock (not counted)"
        : "Tracked inventory",
    ],
  ];

  const stockState =
    sink.stocked === false
      ? "Supplier stock"
      : sink.quantity <= 0
        ? "Out of stock"
        : low
          ? "Low stock"
          : "In stock";
  const STATE_BADGE = {
    "In stock": "border-emerald-500/25 bg-emerald-500/10 text-emerald-400",
    "Low stock": "border-red-500/25 bg-red-500/10 text-red-400",
    "Out of stock": "border-red-500/25 bg-red-500/10 text-red-400",
    "Supplier stock": "border-amber-500/25 bg-amber-500/10 text-amber-400",
  };

  return (
    <div className="grain min-h-screen">
      <AppHeader title="Inventory / Sink" />
      <main className="relative z-10 pb-28 pt-6">
        <Link
          to="/inventory/sinks"
          data-testid="back-to-sinks"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 hover:text-white"
        >
          <ArrowLeft size={14} weight="bold" /> All sinks
        </Link>

        <div
          className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900 p-5"
          data-testid="sink-header-card"
        >
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="font-mono-data text-xs uppercase tracking-widest text-zinc-500">
                {sink.code}
              </div>
              <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                {[sink.brand, sink.model].filter(Boolean).join(" ")}
                <span
                  data-testid="sink-status-badge"
                  className={`ml-3 inline-block rounded-full border px-2.5 py-0.5 align-middle text-[10px] font-semibold uppercase leading-tight tracking-widest ${
                    STATE_BADGE[stockState]
                  }`}
                >
                  {stockState}
                </span>
              </h1>
              {sink.description && (
                <div
                  data-testid="sink-detail-description"
                  className="rte-prose mt-2 max-w-2xl text-sm text-zinc-400"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(sink.description),
                  }}
                />
              )}
            </div>
            <div className="flex gap-2">
              <Button
                data-testid="edit-sink-button"
                onClick={() => navigate(`/inventory/sinks/${id}/edit`)}
                className="h-11 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
              >
                <PencilSimple size={15} weight="bold" className="mr-2" /> Edit
              </Button>
              <Button
                data-testid="delete-sink-button"
                variant="outline"
                onClick={remove}
                className="h-11 border-zinc-700 bg-transparent text-xs uppercase tracking-widest text-red-400 hover:bg-zinc-800"
              >
                <Trash size={15} weight="bold" />
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[
            [
              "On hand",
              sink.stocked === false ? "Supplier stock" : sink.quantity,
              sink.stocked === false
                ? "text-amber-400"
                : low
                  ? "text-red-400"
                  : "text-emerald-400",
            ],
            ["Reserved", reserved, "text-indigo-300"],
            [
              "Min level",
              sink.stocked === false ? "—" : sink.min_level,
              "text-white",
            ],
            ["Photos", photos.length, "text-white"],
          ].map(([k, v, c]) => (
            <div
              key={k}
              data-testid={`sink-detail-stat-${k.toLowerCase().replace(/ /g, "-")}`}
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                {k}
              </div>
              <div className={`font-mono-data mt-1 text-xl font-bold ${c}`}>
                {v}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="divide-y divide-zinc-800 rounded-lg border border-zinc-800 bg-zinc-900">
            {rows.map(([k, v]) => (
              <div
                key={k}
                className="flex items-center justify-between gap-4 px-4 py-3"
              >
                <span className="text-xs uppercase tracking-widest text-zinc-500">
                  {k}
                </span>
                <span
                  data-testid={`sink-detail-${k.toLowerCase().replace(/ /g, "-")}`}
                  className="font-mono-data text-right text-sm font-medium"
                >
                  {v}
                </span>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <ItemLinks kind="sinks" itemId={id} testId="sink-links" />
            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-pricing-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Cost & Sale
                </div>
                {!editingPrice && (
                  <button
                    data-testid="sink-edit-pricing-button"
                    onClick={() => {
                      setPriceDraft({
                        cost: sink.cost ?? "",
                        sale_price: sink.sale_price ?? "",
                      });
                      setEditingPrice(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editingPrice ? (
                <div className="mt-3 space-y-3">
                  {[
                    ["cost", "Cost"],
                    ["sale_price", "Sale price"],
                  ].map(([k, label]) => (
                    <div key={k} className="space-y-1">
                      <span className="block text-[10px] uppercase tracking-widest text-zinc-500">
                        {label}
                      </span>
                      <Input
                        data-testid={`sink-${k}-input`}
                        type="number"
                        step="0.01"
                        min="0"
                        value={priceDraft[k]}
                        onChange={(e) =>
                          setPriceDraft({ ...priceDraft, [k]: e.target.value })
                        }
                        placeholder="—"
                        className="h-11 border-zinc-800 bg-zinc-950"
                      />
                    </div>
                  ))}
                  <div className="flex gap-2">
                    <Button
                      data-testid="sink-save-pricing-button"
                      disabled={savingPrice}
                      onClick={savePricing}
                      className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {savingPrice ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid="sink-cancel-pricing-button"
                      variant="outline"
                      onClick={() => setEditingPrice(false)}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="mt-3 divide-y divide-zinc-800 rounded-md border border-zinc-800 bg-zinc-950">
                  {[
                    ["Cost", sink.cost != null ? `$${sink.cost}` : "—"],
                    [
                      "Sale price",
                      sink.sale_price != null ? `$${sink.sale_price}` : "—",
                    ],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex items-center justify-between gap-4 px-3 py-2.5"
                    >
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {k}
                      </span>
                      <span
                        data-testid={`sink-detail-${k.toLowerCase().replace(/ /g, "-")}`}
                        className="font-mono-data text-right text-sm font-medium"
                      >
                        {v}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-notes-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Notes
                </div>
                {!editingNotes && (
                  <button
                    data-testid="sink-edit-notes-button"
                    onClick={() => {
                      setNotesDraft(sink.notes || "");
                      setEditingNotes(true);
                    }}
                    className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-indigo-300 hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> Edit
                  </button>
                )}
              </div>
              {editingNotes ? (
                <div className="mt-3">
                  <Textarea
                    data-testid="sink-notes-input"
                    autoFocus
                    rows={4}
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    className="border-zinc-800 bg-zinc-950"
                  />
                  <div className="mt-3 flex gap-2">
                    <Button
                      data-testid="sink-save-notes-button"
                      disabled={savingNotes}
                      onClick={saveNotes}
                      className="h-10 bg-indigo-500 text-xs font-bold uppercase tracking-widest text-white hover:bg-indigo-400"
                    >
                      {savingNotes ? "Saving…" : "Save"}
                    </Button>
                    <Button
                      data-testid="sink-cancel-notes-button"
                      variant="outline"
                      onClick={() => setEditingNotes(false)}
                      className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <p
                  className="mt-2 whitespace-pre-wrap text-sm text-zinc-300"
                  data-testid="sink-detail-notes"
                >
                  {sink.notes || "—"}
                </p>
              )}
            </div>

            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-photos-panel"
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Photos ({photos.length})
                </div>
                <Button
                  data-testid="sink-add-photos"
                  variant="outline"
                  disabled={busy}
                  onClick={() => inputRef.current?.click()}
                  className="h-10 border-zinc-700 bg-transparent text-xs uppercase tracking-widest hover:bg-zinc-800"
                >
                  <Camera size={14} weight="bold" className="mr-2" />
                  {busy ? "Uploading…" : "Add Photos"}
                </Button>
                <input
                  ref={inputRef}
                  data-testid="sink-photo-input"
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={upload}
                />
              </div>
              <PhotoGallery
                photos={photos}
                onRemove={removePhoto}
                onRevertHd={revertHd}
                onReorder={reorderPhotos}
                onSetCover={setCover}
                angleOptions={SINK_ANGLES}
                onAngle={setAngle}
                testPrefix="sink-photo"
              />
              {photos.length > 0 && (
                <div
                  className="mt-3 flex flex-wrap items-center gap-2 text-[11px]"
                  data-testid="sink-angle-coverage"
                >
                  <span className="uppercase tracking-widest text-zinc-500">
                    360° spin
                  </span>
                  {SINK_ANGLES.map((a) => (
                    <span
                      key={a.value}
                      data-testid={`sink-angle-coverage-${a.value}`}
                      className={`rounded-full border px-2.5 py-0.5 font-semibold ${
                        tagged.has(a.value)
                          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                          : "border-zinc-700 text-zinc-500"
                      }`}
                    >
                      {tagged.has(a.value) ? "✓ " : ""}
                      {a.label}
                    </span>
                  ))}
                  <span className="text-zinc-500">
                    {tagged.size === SINK_ANGLES.length
                      ? "— customers get the full spin"
                      : "— tag each photo so the portal spins in order"}
                  </span>
                </div>
              )}
            </div>

            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-holding-panel"
            >
              <div className="flex items-center gap-2">
                <Drop size={16} weight="bold" className="text-indigo-300" />
                <div className="text-xs uppercase tracking-widest text-zinc-500">
                  Held for work ({(sink.allocations || []).length})
                </div>
              </div>
              <div className="mt-3 space-y-2">
                {(sink.allocations || []).length === 0 && (
                  <p className="text-sm text-zinc-500">
                    Not reserved to any job or hold.
                  </p>
                )}
                {(sink.allocations || []).map((a) => (
                  <div
                    key={a.id}
                    data-testid={`sink-holder-${a.id}`}
                    className="flex items-center gap-3 rounded-md border border-zinc-800 bg-zinc-950 p-3"
                  >
                    <Link
                      to={
                        a.entity === "job"
                          ? `/projects/${a.entity_id}?tab=field`
                          : `/reservation/${a.entity_id}`
                      }
                      className="min-w-0 flex-1 hover:opacity-80"
                    >
                      <span className="font-mono-data block text-xs text-yellow-500">
                        {a.label || a.entity}
                      </span>
                      <span className="block text-sm font-semibold">
                        {a.entity === "job" ? "Job" : "Material hold"} · {a.qty}{" "}
                        sink
                        {a.qty === 1 ? "" : "s"}
                      </span>
                      <span className="block text-xs text-zinc-500">
                        Reserved {(a.assigned_at || "").slice(0, 10)}
                        {a.assigned_by ? ` by ${nameOf(a.assigned_by)}` : ""}
                      </span>
                    </Link>
                    <button
                      data-testid={`sink-release-${a.id}`}
                      onClick={() => release(a)}
                      className="text-[11px] uppercase tracking-widest text-zinc-500 hover:text-red-400"
                    >
                      Release
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div
              className="rounded-lg border border-zinc-800 bg-zinc-900 p-4"
              data-testid="sink-history-panel"
            >
              <div className="text-xs uppercase tracking-widest text-zinc-500">
                History
              </div>
              <div className="mt-2 space-y-1 text-sm text-zinc-400">
                <div>
                  Added {(sink.created_at || "").slice(0, 10)}
                  {sink.created_by ? ` by ${nameOf(sink.created_by)}` : ""}
                </div>
                <div>Last updated {(sink.updated_at || "").slice(0, 10)}</div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
